import { getLangFromLocale } from '@transferwise/components';
import { toSeconds, toDays, toHours, toMinutes, parse } from 'duration-fns';
import { createIntl, createIntlCache } from 'react-intl';
import { SpeedMessages } from '../../localisation/messages/speed';
const cache = createIntlCache();
/*
  Rounding Values
    20 seconds
    10 minutes
    3 hours
*/
const SECONDS_ROUNDING_LIMIT = 20;
const MINUTES_ROUNDING_LIMIT = 10;
const HOURS_ROUNDING_LIMIT = 3;
var TranslationKeys;
(function (TranslationKeys) {
    TranslationKeys["Unavailable"] = "unavailable";
    TranslationKeys["SameDay"] = "same-day";
    TranslationKeys["Day"] = "day";
    TranslationKeys["Hour"] = "hour";
    TranslationKeys["Minute"] = "minute";
    TranslationKeys["Second"] = "second";
    TranslationKeys["Within"] = "within";
    TranslationKeys["Instant"] = "instant";
})(TranslationKeys || (TranslationKeys = {}));
const TimeUnitTranslationKeyMap = {
    days: TranslationKeys.Day,
    hours: TranslationKeys.Hour,
    minutes: TranslationKeys.Minute,
    seconds: TranslationKeys.Second,
};
export const getSpeedLabel = (isoMin, isoMax, locale) => {
    try {
        isoMin && parse(isoMin);
        isoMax && parse(isoMax);
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.error('Invalid ISO duration format. Please provide a valid ISO duration.', error);
        return translateSpeedLabel(TranslationKeys.Unavailable, locale);
    }
    if (!isoMin && !isoMax) {
        // If both min and isoMax are empty
        return translateSpeedLabel(TranslationKeys.Unavailable, locale);
    }
    else if (!isoMin && isoMax) {
        // If isoMin is empty and isoMax has value
        return withMaxAndNoMin(isoMax, locale);
    }
    else if (isoMin && !isoMax) {
        // If isoMin has value and isoMax is empty
        return withMinAndNoMax(isoMin, locale);
    }
    else if (isoMin && isoMax) {
        // If both isoMin and isoMax have values
        return withMinAndMax(isoMin, isoMax, locale);
    }
    return translateSpeedLabel(TranslationKeys.Unavailable, locale);
};
const withMaxAndNoMin = (isoMax, locale) => {
    if (isoMax === 'P0D') {
        return translateSpeedLabel(TranslationKeys.SameDay, locale);
    }
    if (isoMax === 'PT0S') {
        return translateSpeedLabel(TranslationKeys.Instant, locale);
    }
    const durationToSeconds = Math.floor(toSeconds(isoMax));
    const durationInDays = Math.floor(toDays(isoMax));
    const durationInHours = Math.floor(toHours(isoMax));
    const durationInMinutes = Math.floor(toMinutes(isoMax));
    if (durationToSeconds <= 20) {
        return `${translateSpeedLabel(TranslationKeys.Within, locale)} ${translateSpeedLabel(TranslationKeys.Second, locale, {
            seconds: '',
        })}`;
    }
    const timeUnits = [
        {
            value: durationInDays,
            label: translateSpeedLabel(TranslationKeys.Day, locale, {
                days: durationInDays,
            }),
            limit: 0,
        },
        {
            value: durationInHours,
            label: translateSpeedLabel(TranslationKeys.Hour, locale, {
                hours: durationInHours <= HOURS_ROUNDING_LIMIT ? '' : durationInHours,
            }),
        },
        {
            value: durationInMinutes,
            label: translateSpeedLabel(TranslationKeys.Minute, locale, {
                minutes: durationInMinutes <= MINUTES_ROUNDING_LIMIT ? '' : durationInMinutes,
            }),
            limit: MINUTES_ROUNDING_LIMIT,
        },
        {
            value: durationToSeconds,
            label: translateSpeedLabel(TranslationKeys.Second, locale, {
                seconds: durationToSeconds <= SECONDS_ROUNDING_LIMIT ? '' : durationToSeconds,
            }),
            limit: SECONDS_ROUNDING_LIMIT,
        },
    ];
    for (const unit of timeUnits) {
        if (unit.value >= 1) {
            return `${translateSpeedLabel(TranslationKeys.Within, locale)} ${unit.label}`;
        }
    }
    return translateSpeedLabel(TranslationKeys.Unavailable, locale);
};
const withMinAndNoMax = (isoMin, locale) => {
    const durationInDays = Math.floor(toDays(isoMin));
    const durationInHours = Math.floor(toHours(isoMin));
    const durationInMinutes = Math.floor(toMinutes(isoMin));
    const durationToSeconds = Math.floor(toSeconds(isoMin));
    const timeUnits = [
        {
            value: durationInDays,
            lable: translateSpeedLabel(TranslationKeys.Day, locale, { days: '' }),
        },
        {
            value: durationInHours,
            lable: translateSpeedLabel(TranslationKeys.Hour, locale, { hours: '' }),
        },
        {
            value: durationInMinutes,
            lable: translateSpeedLabel(TranslationKeys.Minute, locale, {
                minutes: '',
            }),
        },
        {
            value: durationToSeconds,
            lable: translateSpeedLabel(TranslationKeys.Second, locale, {
                seconds: '',
            }),
        },
    ];
    for (const unit of timeUnits) {
        if (unit.value >= 1) {
            return `${unit.value}+ ${unit.lable}`;
        }
    }
    return translateSpeedLabel(TranslationKeys.Unavailable, locale);
};
const withMinAndMax = (isoMin, isoMax, locale) => {
    const minDurationInSeconds = Math.floor(toSeconds(isoMin));
    const maxDurationInSeconds = Math.floor(toSeconds(isoMax));
    if (minDurationInSeconds > maxDurationInSeconds) {
        return translateSpeedLabel(TranslationKeys.Unavailable, locale);
    }
    if (minDurationInSeconds === 0 && maxDurationInSeconds === 0) {
        return translateSpeedLabel(TranslationKeys.Instant, locale);
    }
    if (maxDurationInSeconds < 20) {
        return `${translateSpeedLabel(TranslationKeys.Within, locale)} ${translateSpeedLabel(TranslationKeys.Second, locale, {
            seconds: '',
        })}`;
    }
    const parsedMin = parseDuration(isoMin);
    const parsedMax = parseDuration(isoMax);
    // if they are the equal
    if (minDurationInSeconds === maxDurationInSeconds) {
        return translateSpeedLabel(TimeUnitTranslationKeyMap[parsedMax.unit], locale, {
            [parsedMax.unit]: `${parsedMax.val}`,
        });
    }
    if (parsedMin.unit === parsedMax.unit) {
        return translateSpeedLabel(TimeUnitTranslationKeyMap[parsedMax.unit], locale, {
            [parsedMax.unit]: `${parsedMin.val}-${parsedMax.val}`,
        });
        // if not equal and have the same unit
    }
    else {
        // if not equal and doesn't have the same unit
        return `${translateSpeedLabel(TranslationKeys.Within, locale)} ${translateSpeedLabel(TimeUnitTranslationKeyMap[parsedMax.unit], locale, {
            [parsedMax.unit]: `${parsedMax.val}`,
        })}`;
    }
};
function parseDuration(isoDuration) {
    const durationInDays = Math.floor(toDays(isoDuration));
    const durationInHours = Math.floor(toHours(isoDuration));
    const durationInMinutes = Math.floor(toMinutes(isoDuration));
    const durationToSeconds = Math.floor(toSeconds(isoDuration));
    if (durationInDays >= 1) {
        return {
            val: durationInDays,
            unit: 'days',
        };
    }
    else if (durationInHours >= 1) {
        return {
            val: durationInHours,
            unit: 'hours',
        };
    }
    else if (durationInMinutes >= 1) {
        return {
            val: durationInMinutes,
            unit: 'minutes',
        };
    }
    else {
        return {
            val: durationToSeconds,
            unit: 'seconds',
        };
    }
}
function translateSpeedLabel(key, locale, options) {
    const lang = getLangFromLocale(locale) === 'zh'
        ? locale
        : getLangFromLocale(locale) || 'en';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { formatMessage } = createIntl({
        locale: lang,
        messages: Object.assign(Object.assign({}, SpeedMessages.en), SpeedMessages[lang]) || SpeedMessages.en,
    }, cache);
    const formattedMessage = options
        ? formatMessage({ id: key }, options).trim()
        : formatMessage({ id: key });
    return formattedMessage.toLowerCase();
}
